/**
 * Internal identity check for displayed transportation activity vs emissions.
 * Does not recompute fleet emission factors.
 */

export type TransportActivityRole = 'calculation_input' | 'contextual';

export type TransportReconciliationStatus =
  | 'compatible'
  | 'contextual_activity'
  | 'unresolved';

export interface TransportActivityDiagnostic {
  sectorCode: 'ldv' | 'hdv';
  emissionsMt: number | null;
  displayedActivity: number | null;
  activityUnit: string;
  impliedIntensityGPerUnit: number | null;
  intensityPublishable: boolean;
  activitySource: string;
  emissionsSource: string;
  smoothingApplied: boolean | null;
  activityRole: TransportActivityRole | null;
  reconciliationStatus: TransportReconciliationStatus;
  note: string;
}

const GRAMS_PER_MT = 1e6;

export function impliedIntensityGramsPerUnit(
  emissionsMt: number | null,
  activity: number | null
): number | null {
  if (emissionsMt == null || activity == null) return null;
  if (!Number.isFinite(emissionsMt) || !Number.isFinite(activity) || activity === 0) return null;
  return (emissionsMt * GRAMS_PER_MT) / activity;
}

export function reconcileTransportActivity(input: {
  sectorCode: 'ldv' | 'hdv';
  emissionsMt: number | null;
  displayedActivity: number | null;
  activityUnit: string;
  activitySource: string;
  emissionsSource: string;
  smoothingApplied: boolean | null;
  activityRole: TransportActivityRole | null;
}): TransportActivityDiagnostic {
  const implied = impliedIntensityGramsPerUnit(input.emissionsMt, input.displayedActivity);
  if (input.displayedActivity == null || input.emissionsMt == null) {
    return {
      ...input,
      impliedIntensityGPerUnit: implied,
      intensityPublishable: false,
      reconciliationStatus: 'unresolved',
      note:
        input.sectorCode === 'hdv'
          ? 'HDV activity or emissions are missing for this year. Missing activity is not converted to zero.'
          : 'LDV activity or emissions are missing for this year. Missing activity is not converted to zero.',
    };
  }
  if (input.activityRole === 'contextual' || input.smoothingApplied === true) {
    return {
      ...input,
      impliedIntensityGPerUnit: implied,
      intensityPublishable: false,
      reconciliationStatus: 'contextual_activity',
      note:
        'Displayed vehicle miles are contextual fleet activity from the EMFAC HDV class query. They are not the miles that produced the displayed HDV emissions for this year because the software replaced the current-year fleet-model emissions with the previous year’s emissions when the year-over-year change exceeded 50%, or because the emissions are backcast. Implied intensity from this pair is not a member-facing metric.',
    };
  }
  return {
    ...input,
    impliedIntensityGPerUnit: implied,
    intensityPublishable: input.activityRole === 'calculation_input',
    reconciliationStatus: 'compatible',
    note:
      input.sectorCode === 'hdv'
        ? 'Displayed HDV miles are the summed emfac2025_fleet.total_vmt rows for class HDV that enter the unsmoothed fleet-model identity for this year, before integer rounding.'
        : 'Displayed LDV miles are the ClimatePlans combined residential-activity series used by the GHG calculation for this year.',
  };
}

export function hdvMemberActivityCaption(diagnostic: TransportActivityDiagnostic | null): string {
  if (!diagnostic) {
    return 'Heavy-duty miles are copied from emfac2025_fleet.total_vmt when that series is attached. Missing miles are not filled with zero.';
  }
  if (diagnostic.reconciliationStatus === 'contextual_activity') {
    return 'Heavy-duty miles shown here are contextual EMFAC fleet activity for class HDV. They are not the miles that produced the displayed HDV emissions total for this inventory year.';
  }
  if (diagnostic.reconciliationStatus === 'compatible') {
    return 'Heavy-duty miles shown here are the EMFAC fleet total_vmt sum for class HDV in this city, which is the activity in the unsmoothed HDV emissions identity for this year.';
  }
  return diagnostic.note;
}
